/**
 * Ruemood Lazy Loading Utility
 * Clean, semantic lazy loading implementation using Intersection Observer API
 * No hacks, no overrides, proper progressive enhancement
 */

class RuemoodLazyLoader {
  constructor() {
    this.observers = new Map();
    this.loadedSections = new Set();
    
    // Check for Intersection Observer support
    this.isSupported = 'IntersectionObserver' in window;
    
    if (!this.isSupported) {
      console.warn('IntersectionObserver not supported. Loading all content immediately.');
    }
  }

  /**
   * General purpose lazy loader with Intersection Observer
   * @param {string} selector - CSS selector for elements to observe
   * @param {Function} callback - Function to call when element is visible
   * @param {Object} options - IntersectionObserver options
   */
  observe(selector, callback, options = {}) {
    if (!this.isSupported) {
      // Fallback: load immediately if not supported
      document.querySelectorAll(selector).forEach(callback);
      return null;
    }

    const defaultOptions = {
      rootMargin: '200px',
      threshold: 0.01
    };

    const observerOptions = { ...defaultOptions, ...options };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          callback(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe all matching elements
    const elements = document.querySelectorAll(selector);
    elements.forEach(el => observer.observe(el));

    // Store observer reference for cleanup if needed
    const observerId = `observer-${Date.now()}`;
    this.observers.set(observerId, observer);

    return observer;
  }

  /**
   * Load script dynamically when needed
   * @param {string} src - Script URL
   * @param {Function} callback - Callback when loaded
   */
  loadScript(src, callback) {
    // Check if script already loaded
    if (document.querySelector(`script[src="${src}"]`)) {
      if (callback) callback();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    
    script.onload = () => {
      if (callback) callback();
    };
    
    script.onerror = () => {
      console.error(`Failed to load script: ${src}`);
    };
    
    document.head.appendChild(script);
  }

  /**
   * Create placeholder element with optional skeleton loader
   * @param {HTMLElement} container - Container element
   * @param {Object} config - Configuration options
   */
  createPlaceholder(container, config = {}) {
    const {
      height = 'auto',
      showSkeleton = false,
      skeletonClass = 'skeleton-loader'
    } = config;

    container.style.minHeight = height;
    
    if (showSkeleton) {
      container.classList.add(skeletonClass);
    }
  }

  /**
   * Remove placeholder and reveal content
   * @param {HTMLElement} container - Container element
   */
  removePlaceholder(container) {
    container.style.minHeight = '';
    container.classList.remove('skeleton-loader');
  }

  /**
   * Cleanup all observers
   */
  cleanup() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
  }
}

// Initialize global instance
if (!window.RuemoodLazy) {
  window.RuemoodLazy = new RuemoodLazyLoader();
}

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RuemoodLazyLoader;
}

